import { inject, Injectable } from '@angular/core';
import { BaseComponent } from '../../components/base/base.component';
import { byType, extractType, isValidFieldName, isPageable, isNumberType, checkSpecialCharacters, isJSONDate, isString, formatVariableIconClass, getClonedObject, MarkupManagerService } from '@wms/core';
import { difference, endsWith, escapeRegExp, extend, filter, forEach, get, includes, intersection, isEmpty, isObject, keys, map, split, toUpper, head, last, isArray, remove, find, findIndex, isFunction } from 'lodash-es';
import { PrefabService, TypeFactoryService } from '@wms/services';
import {
  getDuplicateVariables,
  getVariableServiceType,
  getServiceTypesByCollectionType,
  BaseVariableService
} from '@wms/variables';
import { stringStartsWith, isDefined, getTypes } from '@wm/core';
import { getWidgetPropsByType, getWidgetRegistry, propFactory as PropertiesFactory } from '@wm/components-base';
import { PageWsService } from '@wms/workspace';
@Injectable({
  providedIn: 'root'
})
export class BindTreeUtilsService extends BaseComponent {
  expressionRegExp = /\s|\+|-|\*|\/|\|/;
  OWNER_APP = 'App';
  OWNER_PAGE = 'Page';
  LABEL_LIST = '(List)';
  LABEL_OBJECT = '(Object)';
  defaultPrimitiveTypes = ['string', 'boolean', 'number', 'date', 'datetime', 'timestamp', 'time'];
  boundEntityValue: any;
  isCurrBoundValue: any;
  isPrevBoundValue: any;
  isBoundValue: any;
  boundedProperty: any;
  validateBinding: any;
  boundEntityParams: any;
  typeService: TypeFactoryService;
  pageWsService: PageWsService;
  selectedPage: string;
  selectedPageDom: any;
  prefabService: PrefabService
  variablesTreeCopy: any;
  variableService:BaseVariableService;

  constructor(private markupManager: MarkupManagerService) {
    super();
    this.typeService = inject(TypeFactoryService);
    this.pageWsService = inject(PageWsService);
    this.prefabService = inject(PrefabService);
    this.variableService = inject(BaseVariableService);
  }


  /*
   * Returns base structure for variables tree
   * */
  getRootStructure(variablesCollection?: any) {
    let rootNode: any = [];
    if (variablesCollection) {
      //Use selectedPage instead of activePageName. We are setting selected page when we open bind dialog outside the page context
      forEach([this.selectedPage || this.pageWsService.activePageName, this.OWNER_APP], (context) => {
        if (!isEmpty(variablesCollection[context])) {
          rootNode.push(this.getRootObj(context === this.OWNER_APP ? this.OWNER_APP : this.OWNER_PAGE));
        }
      });
    }
    return variablesCollection ? rootNode : [this.getRootObj(this.OWNER_PAGE), this.getRootObj(this.OWNER_APP)];
  }

  /*
* Function to get root object for a context
* @param context context for which root object is required
* */
  getRootObj(context: any) {
    return {
      'idkey': 'LABEL_' + toUpper(context),
      'id': context,
      'children': []
    };
  }

  /**
   * returns the label to be displayed against tree nodes as info
   * @param type dataType of the node
   * @param bindingType object/list type of node
   * @param skipExtraction flag to toggle extraction
   * @returns {string}
   */
  getNodeInfoLabel(type: any, bindingType: any, skipExtraction?: any) {
    let nodeLabel = skipExtraction ? type : this.getTypeForDisplay(type);
    if (bindingType === 'array') {
      nodeLabel = nodeLabel + ' ' + this.LABEL_LIST;
    } else if (bindingType === 'object') {
      nodeLabel = nodeLabel + ' ' + this.LABEL_OBJECT;
    } else if (bindingType === 'all') {
      nodeLabel = ' (Object, List)';
    }
    return nodeLabel;
  }

  /**
   * formats and returns the type info to be displayed in the UI
   * @param type
   * @returns {string}
   */
  getTypeForDisplay(type: any) {
    return extractType(type);
  }

  /*
   * returns true if the name is in path of bound entity
   * @param name, id/ name of the current node
   */
  isInBoundPath(name: string) {
    let isInBoundPathRegExp = new RegExp('\\b' + escapeRegExp(name) + '\\b', 'g');
    return (this.isBoundValue && isInBoundPathRegExp.test(this.boundEntityValue));
  }

  /*
  * returns true if the name is the bound entity
  * @param name, id/ name of the current node
  */
  isActiveEntity(name: string) {
    return this.isPrevBoundValue ? false : this.boundedProperty === name;
  }

  //Adds first or last record
  getRecord(recordName: string, variable: any, children: Array<any>) {
    let nodeObj,
      isActive,
      variablePKG = variable.category === 'wm.LiveVariable' ? variable.package : variable.type;
    isActive = this.isActiveEntity(recordName);
    nodeObj = {
      "id": recordName,
      "sub": this.getNodeInfoLabel(variablePKG, 'object'),
      "children": children,
      "class": '',
      "textClass": 'virtual',
      "collapsed": !this.isInBoundPath(variable.name + '.' + recordName),
      "props": {
        "bindable": true,
        "value": variable.name + '.' + recordName,
        "type": "variable",
        "datatype": variablePKG,
        "variableCategory": variable.category,
        "isList": false,
        "bindingType": "object"
      },
      "active": isActive
    };
    if (isActive) {
      this.validateBinding(nodeObj.id, nodeObj.props);
    }
    return nodeObj;
  }

  addFirstAndLastRecord(nodeArr: { id: string; sub: any; children: any; collapsed: boolean; props: { bindable: boolean; value: string; type: string; datatype: any; bindingType: any; category: any; isList: boolean; }; active: any; }[] = []
    , variable: any) {
    let datatype = variable.category === 'wm.LiveVariable' ? variable.package : variable.type,
      firstRecord: any = this.getRecord('firstRecord', variable, this.getTreeForType(datatype, { "parentNodeLabel": variable.name + ".firstRecord", "componentType": "variable", "category": variable.category, "serviceName": variable.liveSource || variable.service })),
      lastRecord: any = this.getRecord('lastRecord', variable, this.getTreeForType(datatype, { "parentNodeLabel": variable.name + ".lastRecord", "componentType": "variable", "category": variable.category, "serviceName": variable.liveSource || variable.service }));
    nodeArr.push(firstRecord);
    nodeArr.push(lastRecord);
  }

  /**
   * returns bindingType based on data-type and isList property
   * @param type data-type of the node
   * @param isList array flag for the node
   * @returns {boolean}
   */
  getBindingType(type: any, isList: boolean, isWidgetTree?: boolean) {
    let bindingType: any,
      dataTypes: any = this.typeService.getTypes()?.[type];

    //For reset service which returns pageable object, get type and isList from content object inside the dataset
    if (!isWidgetTree && dataTypes && isPageable(dataTypes.fields)) {
      type = dataTypes.fields.content.type;
      isList = dataTypes.fields.content.isList;
    }

    if (isList) {
      bindingType = 'array';
    } else if (this.isPrimitiveType(type) || type === 'object') {
      bindingType = '';
    } else if (intersection(this.cleanseTypes(type), ['array', 'object']).length === 2) {
      bindingType = 'all';
    } else {
      bindingType = 'object';
    }
    return bindingType;
  }

  /**
   * converts different types to wm-bindable types. e.g int, integer, byte, etc to number
   * @param types comma separated string of datatypes
   * @param format format of the bound node
   * @returns {Array} array of types
   */
  cleanseTypes(types: string, format?: string) {
    if (!isEmpty(format)) {
      types = types + ',' + format;
    }
    return map(split(types, ','), (i) => {
      i = extractType(i).trim().toLowerCase();
      return i === "list" ? "array" : (isNumberType(i) ? 'number' : i);
    });
  }

  /**
   * returns true if the passed data-type is a primitive, else returns false
   * @param type
   * @returns {boolean}
   */
  isPrimitiveType(type: any) {
    let types = this.typeService.getTypes(),
      primitiveType = get(types, [type, 'primitiveType']),
      typeArr = this.cleanseTypes(type);

    return primitiveType || !difference(typeArr, this.defaultPrimitiveTypes).length;
  }

  /**
   * returns true if the passed data-value is a primitive datatype, else returns false
   * @param type
   * @returns {boolean}
   */
  isPrimitiveValue(value: any) {
    return includes(this.defaultPrimitiveTypes, typeof value);
  }

  /*
  * Return true for list type child node of live variable
  * */
  isListTypeLiveNode(field: any, options: any) {
    return field.isList && options.category === 'wm.LiveVariable' && (field.type !== 'java.lang.Byte');
  }

  /**
   * returns if the passed data-type has fields or not
   * @param type
   * @returns {boolean}
   */
  isEmptyFields(type: any) {
    let types = this.typeService.getTypes(),
      typeObj = get(types, type);
    return typeObj ? isEmpty(typeObj.fields) : true;
  }

  getTreeForType(type: any, options?: any): any  {
    if (!this.typeService.dataTypesCache.has(options.serviceName)) {
      return () : Promise<{ id: any; sub: any; class: string; collapsed: boolean; children: any; hasChildren: any; props: { bindable: boolean; value: any; type: any; datatype: any; bindingType: any; isList: any; nodeType: string; }; active: any; }[]> => new Promise(resolve => {
        this.typeService.getDataTypesByService(options.serviceName, true).then(() => {
          const nodes = this.getTreeForTypeCB(type, options);
          resolve(nodes);
        });
      });
    } else {
      return this.getTreeForTypeCB(type, options);
    }
  }

  /*
   * prepares and returns nodes for the tree structure corresponding to provided type
   * the tree nodes are prepared based on the META data for the 'type' passed to the function
   * @param type, data-type against which the tree structure needs to be prepared.
   * @param options, extra options like:
   * ** parentNodeLabel, name of the parent node for the sub-tree
   * ** componentType, type of component the tree is being prepared for. E.g 'variable', 'widget', etc.
   */
  getTreeForTypeCB(type: any, options?: any) {
    /* sanity check */
    options = options || {};
    let nodes: { id: any; sub: any; class: string; collapsed: boolean; children: any; hasChildren: any; props: { bindable: boolean; value: any; type: any; datatype: any; bindingType: any; isList: any; nodeType: string; }; active: any; }[] = [],
      types = this.typeService.getTypes(),
      nodeObj,
      fields,
      parentNodeLabel = options.parentNodeLabel,
      fieldsToShow = options && options.fieldsToShow;

    if (types && types[type] && types[type].fields) {
      fields = types[type].fields;
      //For reset service which returns pageable object, get fields from content object inside the dataset
      if (isPageable(fields)) {
        fields = types[fields.content.type] && types[fields.content.type].fields;
      }

      forEach(fields, (field, fieldName) => {
        //If fieldsToShow is defined then ignore other nodes while tree construction
        //Exclude the list type nodes for liveVariables
        if ((!isEmpty(fieldsToShow) && !includes(fieldsToShow, fieldName)) || this.isListTypeLiveNode(field, options)) {
          return;
        }
        let bindValue,
          isFieldNameModified = false,
          fieldType = field.type,
          isTreeExpanded,
          hasChildren,
          childNodes: { id: any; sub: any; class: string; collapsed: boolean; children: any; hasChildren: any; props: { bindable: boolean; value: any; type: any; datatype: any; bindingType: any; isList: any; nodeType: string; }; active: any; }[] = [],
          childOptions,
          isActive,
          name,
          propertyBindingType;

        // if the fieldName is not valid, change it to array notation.
        if (!isValidFieldName(fieldName)) {
          fieldName = "['" + fieldName + "']";
          isFieldNameModified = true;
        }

        bindValue = parentNodeLabel ? parentNodeLabel + (isFieldNameModified ? '' : '.') + fieldName : fieldName;
        /*data[$i] is not present in bound variables of live list auto generated labels.
         Remove data[$i] in these cases so that the bound variable is selected. */
        if (this.isBoundValue && this.boundEntityValue.indexOf('data[$i]') < 0 && bindValue.indexOf('.data[$i]') > -1) {
          name = bindValue.replace('.data[$i]', '');
        } else {
          name = bindValue;
        }

        isTreeExpanded = this.isInBoundPath(name);
        isActive = isTreeExpanded ? this.isActiveEntity(fieldName) : false;
        hasChildren = types[fieldType] && types[fieldType].fields && keys(types[fieldType].fields).length;

        // If pre-expansion is required for the tree, get childNodes for the node
        if (isTreeExpanded && !isActive) {
          childOptions = extend({}, options);
          childOptions.parentNodeLabel = bindValue + (field.isList ? '[$i]' : '');
          childNodes = this.getTreeForTypeCB(fieldType, childOptions);
        }
        propertyBindingType = this.getBindingType(field.type, field.isList);

        nodeObj = {
          "id": options.type ? fieldName + ': ' + fieldType.substring(fieldType.lastIndexOf('.') + 1, fieldType.length) : fieldName,
          "sub": this.getNodeInfoLabel(fieldType, propertyBindingType),
          "class": options.icon ? (hasChildren ? 'object' : 'datatype') : '',
          "collapsed": !isTreeExpanded,
          "children": childNodes,
          "hasChildren": hasChildren,
          "props": {
            "bindable": true,
            "value": bindValue,
            "type": options.componentType,
            "datatype": fieldType,
            "bindingType": propertyBindingType,
            "isList": field.isList,
            "nodeType": "type"
          },
          "active": isActive
        };
        if (isActive) {
          this.validateBinding(nodeObj.id, nodeObj.props);
        }
        nodes.push(nodeObj);
      });
    }

    return nodes;
  }

  async getTreeForVar(variable: any, isTreeExpanded?: boolean, options?: any): Promise<any> {
    let modifiedVariableName,
      isActive,
      nodeObj: any,
      isList,
      propertyBindingType,
      nodeArr: { id: string; sub: any; children: any; collapsed: boolean; props: { bindable: boolean; value: string; type: string; datatype: any; bindingType: any; category: any; isList: boolean; }; active: any; }[] = [];

    // if the fieldName is not valid, change it to array notation.
    if (!isValidFieldName(variable.name)) {
      modifiedVariableName = "['" + variable.name + "']";
    }

    switch (variable.category) {
      case 'wm.LiveVariable':
        isActive = isTreeExpanded ? this.isActiveEntity('dataSet') : false;
        modifiedVariableName = modifiedVariableName || variable.name + ".";
        isList = variable.operation === 'read';
        if (isList) {
          this.addFirstAndLastRecord(nodeArr, variable);
          modifiedVariableName = modifiedVariableName + "dataSet[$i]"
        } else {
          modifiedVariableName = modifiedVariableName + "dataSet"
        }
        propertyBindingType = this.getBindingType(variable.package, isList);
        nodeObj = {
          "id": 'dataSet',
          "sub": this.getNodeInfoLabel(variable.package, propertyBindingType),
          "children": this.getTreeForType(variable.package, {
            "parentNodeLabel": modifiedVariableName,
            "componentType": "variable",
            "category": variable.category,
            "serviceName": variable.liveSource
          }),
          "collapsed": !isTreeExpanded,
          "props": {
            "bindable": true,
            "value": variable.name + '.dataSet',
            "type": "variable",
            "datatype": variable.package,
            "bindingType": propertyBindingType,
            "category": variable.category,
            "isList": isList
          },
          "active": isActive
        };
        if(isFunction(nodeObj.children) && isTreeExpanded) {
           await nodeObj.children().then((children: any) => {
             nodeObj.children = children;
          });
        }
        if (isActive) {
          this.validateBinding(nodeObj.id, nodeObj.props);
        }
        nodeArr.push(nodeObj);
        return nodeArr;
      case "wm.Variable":
        modifiedVariableName = modifiedVariableName || variable.name;
        return this.getSubTree({
          'dataSet': variable.dataSet
        }, variable, modifiedVariableName, options);
      case "wm.CrudVariable":
      case "wm.ServiceVariable":
        isActive = isTreeExpanded ? this.isActiveEntity('dataSet') : false;
        if (variable._buildTreeFromDataSet) {
          modifiedVariableName = modifiedVariableName || variable.name;
          return this.getSubTree({
            'dataSet': variable.dataSet
          }, variable, modifiedVariableName);
        }
        if(this.typeService.dataTypesCache.has(variable.service)) {
          // For primitive type of services, prepare tree from dummy dataSet
          if (this.isPrimitiveType(variable.type) || this.isEmptyFields(variable.type)) {
            modifiedVariableName = modifiedVariableName || variable.name;
            return this.getSubTree({
              'dataSet': { 'value': '' }
            }, variable, modifiedVariableName);
          }
        }
        modifiedVariableName = (modifiedVariableName || variable.name + ".") + "dataSet";
        modifiedVariableName = modifiedVariableName + (variable.isList ? "[$i]" : "");
        isList = variable.isList;
        if (isList) {
          this.addFirstAndLastRecord(nodeArr, variable);
        }
        propertyBindingType = this.getBindingType(variable.type, isList);
        nodeObj = {
          "id": 'dataSet',
          "sub": this.getNodeInfoLabel(variable.type, propertyBindingType),
          "children": this.getTreeForType(variable.type, { "parentNodeLabel": modifiedVariableName, "componentType": "variable", "serviceName": variable.service }),
          "collapsed": !isTreeExpanded,
          "props": {
            "bindable": true,
            "value": variable.name + '.dataSet',
            "type": "variable",
            "datatype": variable.type,
            "bindingType": propertyBindingType,
            "category": variable.category,
            "isList": isList
          },
          "active": isActive
        };
        if(isFunction(nodeObj.children) && isTreeExpanded) {
          await nodeObj.children().then((children: any) => {
            nodeObj.children = children;
          });
        }
        if (isActive) {
          this.validateBinding(nodeObj.id, nodeObj.props);
        }
        nodeArr.push(nodeObj);
        return nodeArr;
        case "wm.WebSocketVariable":
          isActive             = isTreeExpanded ? this.isActiveEntity('dataSet') : false;
          modifiedVariableName = (modifiedVariableName || variable.name + ".") + "dataSet";
          modifiedVariableName = modifiedVariableName + (variable.isList ? "[$i]" : "");
          isList               = variable.isList || this.shouldAppendData(variable)
          propertyBindingType  = this.getBindingType(variable.type, isList);
          nodeObj = {
              "id": 'dataSet',
              "sub": this.getNodeInfoLabel(variable.type, propertyBindingType),
              "children": this.getTreeForType(variable.type, {"parentNodeLabel": modifiedVariableName, "componentType": "variable"}),
              "collapsed": !isTreeExpanded,
              "props": {
                  "bindable": true,
                  "value": variable.name + '.dataSet',
                  "type": "variable",
                  "datatype": variable.type,
                  "bindingType": propertyBindingType,
                  "category": variable.category,
                  "isList": isList
              },
              "active": isActive
          };
          if (isActive) {
              this.validateBinding(nodeObj.id, nodeObj.props);
          }
          nodeArr.push(nodeObj);
          if (isList) {
              this.addFirstAndLastRecord(nodeArr, variable);
          }
          return nodeArr;
        default:
        return this.getSubTree({
          'dataSet': variable.dataSet
        }, variable, (modifiedVariableName || variable.name));

    }

  }

  shouldAppendData(variable:any) {
    return variable = variable,
    variable["dataUpdateStrategy"] !== "refresh"
  }

  /*
 * creates and returns a subtree for variable's dataSet object
 * @param dataset current dataset, whose tre object is to be constructed
 * @param variable variable object associated with the dataset
 * @param parentNodesString string name of the previous parent to be appended along with the node properties
 * @param options, this is used to check if the tree is generated for property binding.
 */
  // Todo: Maintain consistency of properties all through out(Use either name or id everywhere)
  getSubTree(dataset: any, variable: any, parentNodesString?: string, options?: any) {
    let dataSetTree: ({ name: any; type: any; id?: undefined; sub?: undefined; class?: undefined; collapsed?: undefined; props?: undefined; active?: undefined; } | { id: any; sub: any; class: string; collapsed: boolean; props: { bindable: boolean; value: any; type: string; datatype: any; variableCategory: any; isList: any; bindingType: any; }; active: any; name?: undefined; type?: undefined; })[] = [],
      isGeneratedType: boolean;
    /* check the dataSet */
    if (Array.isArray(dataset)) {
      if (this.isPrimitiveValue(dataset[0])) {
        return dataSetTree;
      }
      dataset = dataset[0];
      parentNodesString = parentNodesString ? parentNodesString + '[$i]' : '';
    } else if (!isObject(dataset)) {
      return dataSetTree;
    } else if (isEmpty(dataset) && endsWith(parentNodesString, '.dataSet')) {
      //If dataset is empty for variable then construct tree based on variable type
      return this.getTreeForTypeCB(variable.type);
    }

    isGeneratedType = !!get(this.typeService.getTypes(), '["' + variable.type + '"].fields');
    /* push the dataSet into tree */
    forEach(dataset, (fieldValue, fieldName) => {
      let bindValue,
        nodeSubtree,
        datatype,
        nodeObj: any,
        isFieldNameModified = false,
        isTreeExpanded,
        isList,
        propertyBindingType,
        isActive;

      // if the fieldName is not valid, change it to array notation.
      if (!isValidFieldName(fieldName)) {
        fieldName = checkSpecialCharacters(fieldName);
        isFieldNameModified = true;
      }

      bindValue = parentNodesString ? parentNodesString + (isFieldNameModified ? '' : '.') + fieldName : fieldName;
      nodeSubtree = this.getSubTree(fieldValue, variable, bindValue, options);

      /* if types is generated type, search for its datatype in the generated types */
      if (isGeneratedType) {
        datatype = this.typeService.getTypeForExpression('bind:Variables.' + bindValue);
      }
      /* else, fallback to compute type based on the fieldValue */
      if (!datatype) {
        if (Array.isArray(fieldValue) && fieldValue[0]) { //Array is internally an object but object cannot be an array, so first check if the given fieldValue is array and process the type from the list
          datatype = isJSONDate(fieldValue[0]) ? 'date' : typeof fieldValue[0];
        } else if (nodeSubtree.length > 0 || isObject(fieldValue)) {
          datatype = 'object';
        } else if (isJSONDate(fieldValue)) {
          datatype = 'date';
        } else {
          datatype = typeof fieldValue;
        }
      }

      isTreeExpanded = this.isInBoundPath(bindValue);
      isActive = isTreeExpanded ? this.isActiveEntity(fieldName) : false;
      isList = (variable.isList && fieldName === 'dataSet') || Array.isArray(fieldValue);
      propertyBindingType = this.getBindingType(datatype, isList);

      if (options && options.isBindingTree) {
        nodeObj = {
          "name": fieldName,
          "type": this.getNodeInfoLabel(datatype, propertyBindingType)
        }
      } else {
        nodeObj = {
          "id": fieldName,
          "sub": this.getNodeInfoLabel(datatype, propertyBindingType),
          "class": '',
          "collapsed": !isTreeExpanded,
          "props": {
            "bindable": true,
            "value": bindValue,
            "type": "variable",
            "datatype": datatype,
            "variableCategory": variable.category,
            "isList": isList,
            "bindingType": propertyBindingType
          },
          "active": isActive
        };
      }
      if (isActive) {
        this.validateBinding(nodeObj.id, nodeObj.props);
      }
      if (nodeSubtree.length > 0 && options && options.isBindingTree) {
        nodeObj.fields = nodeSubtree;
      } else {
        nodeObj.children = nodeSubtree;
      }
      dataSetTree.push(nodeObj);
    });
    return dataSetTree;
  }

  /*
 * Maps java object data types to primitive data types
 * */
  getPrimitiveTypes(tgtTypes: any[]) {
    let dataTypes = this.typeService.getTypes();
    tgtTypes = tgtTypes.map((type) => {
      if (dataTypes[type] && dataTypes[type].primitiveType) {
        return dataTypes[type].primitiveType.toLowerCase();
      }
      if (type.indexOf("java.") > -1) {
        return type.substr(type.lastIndexOf(".") + 1).toLowerCase();
      }
      return type.toLowerCase();
    });
    return tgtTypes;
  }

  /**
   * returns true if the BindingTypes and dataType is matched
   * @param srcObj type, bindingType of source node
   * @param tgtObj type, bindingType of target node
   * @returns {boolean}
   */
  validateVariableWidgetBinding(srcObj: any, tgtObj: any) {
    //if tgtTypes id of java object type, for example java.lang.String or java.lang.Boolean, then get the primitive type.
    let tgtTypes = this.getPrimitiveTypes(tgtObj.types),
      isValidBindigType;
    //If source and target binding types are defined the check if they are same
    isValidBindigType = !srcObj.bindingType || !tgtObj.bindingType || srcObj.bindingType === 'all' || srcObj.bindingType === tgtObj.bindingType;
    // loop over the source types.
    return isValidBindigType && (intersection(srcObj.types, tgtTypes).length || includes(srcObj.types, tgtObj.bindingType));
  }
  //Function to set bound entity.
  setBindMapConfig(boundParams: any, valBinding: any) {
    this.boundEntityParams = boundParams;
    this.isCurrBoundValue = isString(boundParams.boundEntity) && ((boundParams.boundEntity.indexOf('bind:') > -1 && !this.expressionRegExp.test(boundParams.boundEntity)) || boundParams.boundEntity.indexOf('resources/') > -1);
    this.isPrevBoundValue = !this.isCurrBoundValue && !!boundParams.prevBoundEntity && ((boundParams.prevBoundEntity.indexOf('bind:') > -1 && !this.expressionRegExp.test(boundParams.prevBoundEntity)) || boundParams.prevBoundEntity.indexOf('resources/') > -1);
    this.isBoundValue = this.isCurrBoundValue || this.isPrevBoundValue;
    this.boundEntityValue = this.isPrevBoundValue ? boundParams.prevBoundEntity : boundParams.boundEntity;
    this.boundedProperty = this.isBoundValue ? this.boundEntityValue.split(".").pop() : undefined;
    this.validateBinding = valBinding;
  }

  /*
 * returns true if variable is not of call type else false
 * @param variableCategory, category of the variable
 */
  isCallVariable(variableCategory: string) {
    return (variableCategory === 'wm.NavigationVariable' || variableCategory === 'wm.NotificationVariable' || variableCategory === 'wm.TimerVariable');
  }

  /*
   * returns true if dialog is in variableProperty bind mode and supplied variable name matches the activeProperty owner
   * @param variableName, name of the variable
   */
  isActiveVariable(variableName: any) {
    return (this.boundEntityParams?.bindMode === 'variable' && this.boundEntityParams.activeProperty.propertyOwnerName === variableName);
  }

  /*
   * returns the index of a node with specified id in a tree.
   * if not found, pushes the node and returns its index
   * @param nodeId, node id
   * @param tree, the tree
   * @param locale, locale from which to pick the localized node label
   */
  getNodeIndex(nodeId: any, tree: any, locale: any) {
    let nodeIndex = null;
    locale = locale || this.locale;
    forEach(tree, function (nodeObject, index) {
      if (nodeObject.id === nodeId) {
        nodeIndex = index;
      }
    });
    /* if nodeIndex not found push the nodeId */
    if (nodeIndex === null) {
      nodeIndex = tree.push({
        "id": nodeId,
        "label": locale[nodeId] || nodeId,
        "children": [],
        "props": { "bindable": false }
      }) - 1;
    }

    return nodeIndex;
  }

  /*Function to get index of node in tree for a given owner
   * @param variablesTree the tree structure to which index need to be found
   * @param owner owner of the variable i.e app/page
   */
  getOwnerIndex(variablesTree: any, owner: any) {
    let index = findIndex(variablesTree, { 'id': owner });
    if (index < 0) {
      if (owner === this.OWNER_PAGE) {
        variablesTree.unshift(this.getRootObj(owner));
      } else {
        variablesTree.push(this.getRootObj(owner));
      }
      index = findIndex(variablesTree, { 'id': owner });
    }
    return index;
  }

  /*
   *Pushes given variable into variable tree
   * @param variable the variable to be pushed into tree
   * @param isTreeExpanded flag to decide if tree has to be expanded/collapsed
   * @param variablesTree the variables tree
   * @param isWorkspaceTree flag to decide if tree is for workspace
   * @param duplicates array of duplicate variable names
   * */
  addNodeToTree(variable: any, isTreeExpanded: any, variablesTree: any, source: any, duplicates?: any, fetchActions?: boolean) {
    let categoryNodeIndex,
      name = variable.name,
      isDuplicate,
      isActive,
      isWorkspaceTree = source === 'workSpace',
      serviceType = getVariableServiceType(variable),
      isDataTypeService = includes(getServiceTypesByCollectionType('data'), serviceType);

    if (isWorkspaceTree || ((isDataTypeService || fetchActions) && !this.isActiveVariable(name))) {
      categoryNodeIndex = this.getNodeIndex(serviceType, variablesTree, this.locale.LABEL_VARIABLE_SERVICETYPE);
      duplicates = duplicates || getDuplicateVariables();
      isDuplicate = includes(duplicates, name);
      // if the fieldName is not valid, change it to array notation.
      if (!isValidFieldName(name)) {
        name = "['" + name + "']";
      }
      isTreeExpanded = this.isInBoundPath(name);
      isActive = isTreeExpanded ? this.isActiveEntity(name) : false;
      variablesTree[categoryNodeIndex].children.push({
        "id": name,
        "searchable": true,
        "class": isDuplicate ? 'wms wms-warning' : 'wms-' + formatVariableIconClass(variable.category),
        "title": isDuplicate ? this.locale.MESSAGE_ERROR_DUPLICATE_VARIABLE : '',
        "children": !isWorkspaceTree ? this.getTypesBeforeGetTreeForVar(variable, isTreeExpanded) : {},
        "collapsed": !isTreeExpanded,
        "props": !isWorkspaceTree ? {
          "bindable": false,
          "value": name,
          "type": "variable",
          "datatype": variable.category,
          "category": variable.category
        } : {},
        "active": isActive
      });
    }
    this.variablesTreeCopy = variablesTree
  }

  getTypesBeforeGetTreeForVar(variable:any, isTreeExpanded:any){
    let service =variable.liveSource || variable.service
    if (service && !this.typeService.dataTypesCache.has(service)) {
      return () : Promise<{ id: any; sub: any; class: string; collapsed: boolean; children: any; hasChildren: any; props: { bindable: boolean; value: any; type: any; datatype: any; bindingType: any; isList: any; nodeType: string; }; active: any; }[]> => new Promise(resolve => {
        this.typeService.getDataTypesByService(service, true).then(() => {
          const nodes = this.getTreeForVar(variable, isTreeExpanded);
          resolve(nodes);
        });
      });
    } else {
      return this.getTreeForVar(variable, isTreeExpanded);
    }

  }

  /*
   * pushes variables out of given collection into variable tree
   * @param variables the collection of variables
   * @param isTreeExpanded flag to decide if tree has to be expanded/collapsed
   * @param variablesTree the variables tree
   * @param isWorkspaceTree flag to decide if tree is for workspace
   */
  createVariablesTreeObject(variables: any, isTreeExpanded: any, variablesTree: any, source?: any, fetchActions?: boolean|any) {
    let duplicates = getDuplicateVariables();
    forEach(variables, (variable) => {
      if (variable) {
        this.addNodeToTree(variable, isTreeExpanded, variablesTree, source, duplicates, fetchActions);
      }
    });
  }

  /*
   * Initializes variables and creates variable-tree
   * @param variablesCollection collection received from the Variables service
   */
  buildVariables(variablesCollection: any, variablesTree: any, fetchActions?: boolean|any, source?: any) {
    let context;
    //Use selectedPage instead of activePageName. We are setting selected page when we open bind dialog outside the page context
    forEach(variablesTree, (item, index) => {
      context = item.id === this.OWNER_APP ? this.OWNER_APP : (this.selectedPage || this.pageWsService.activePageName);
      if (variablesCollection[context]) {
        this.createVariablesTreeObject(variablesCollection[context], false, variablesTree[index].children, source, fetchActions);
      }
    });
  }

  /**
   * Returns an array of children node objects (compatible to wm-studio-tree directive) against the passed node object
   * @param node tree node for which children nodes are to be fetched
   * @param options extra options for the child tree
   * @returns {*} array of children node objects
   */
  getChildrenForNode(node: any, options: any) {
    return new Promise(async (resolve, reject)=>{
      if(node.prefabname){
       const widgets = byType('form-widgets')
       const config: any = await this.prefabService.getConfig(node.prefabname, true)
       const widget = get(widgets, node.id)
       Object.assign(widget.widgetProps, config.properties)
       const widgetTree = this.getWidgetPropertiesChildTree(widget, null, this.variablesTreeCopy)
       resolve(widgetTree)
      }
      else{
        let types = this.typeService.getTypes(),
          nodeProps = node.props,
          componentType = nodeProps?.type,
          datatype = nodeProps?.datatype,
          typeDef = types[datatype],
          widgetName = head(split(nodeProps?.value, '.')),
          fieldsToShow;

        //For form/liveform's dataoutput show fields which are selected
        if (node.id === 'dataoutput') {
          fieldsToShow = this.getFieldsToShow(widgetName);
        }

        options = extend(options || {}, {
          parentNodeLabel: nodeProps?.value + (nodeProps?.isList ? '[$i]' : ''),
          componentType: componentType,
          fieldsToShow: fieldsToShow
        });

        if (typeDef && typeDef.fields) {
          resolve(this.getTreeForTypeCB(datatype, options));
        }
      }
    })
  }

  //Returns list of fields names which are included in a form/liveForm
  getFieldsToShow(widgetName: any) {
    return map(this.getWidgetsToShow(widgetName), ($is: any) => {
      return last(split($is.key, '.'));
    });
  }

  getWidgetsToShow(widgetName: any): any {
    let widgetsToShow: Array<any> = [];
    let widgetEl: any = $('[name=' + widgetName + ']');
    if (!widgetEl.length) {
      let markup = this.markupManager.processedMarkupForDialog;
      widgetEl = markup?.find('[name=' + widgetName + ']');
    }
    // widgetsToShow will be invoked to list all the formfields which are its children. Hence not to add this widget again just add an attribute which tells the widget is already listed.
    widgetEl.attr('is-widget-listed', true);
    let widgetsRendered = widgetEl.find('[widget-id]');
    if (!widgetsRendered.length) {
      // this is executed when partial is rendered in container inside a page.
      widgetsRendered = widgetEl.find('[widgettype]');
    }
    widgetsRendered.not('[name*="_formWidget"]').each((i: number, el: any) => {
      let _is: any,
        target: any = el;
      // If widgets are inside prefab, do not add it to widgets tree
      if ($(target).parents('[widgetType="wm-prefab"]').length) {
        return;
      }
      if (!target.widget) {
        let registryById = getWidgetRegistry();
        target.widget = registryById.get(target.getAttribute('widget-id'));
      }
      if (target.widget) {
        _is = target.widget;
        if (_is.name && _is.widgetType && (includes(getTypes('form-widgets'), _is.widgetType) || includes(['wm-form-field', 'wm-filter-field'], _is.widgetType))) {
          widgetsToShow.push(_is);
          // if there are any form/list widgets are shown as children to this parent who already has this is-widget-listed attribute,
          // these children will be not shown as separate entity parallel to parent and will only be listed under the parent on which this attr is set.
          if (this.isFormOrLiveWidgetType(_is.widgetType)) {
            _is.$element.attr('is-widget-listed', true);
          }
        }
      }
    });
    // returning only the widgets with is-widget-listed attribute but not considering the children or itself having the attr.
    let filteredWidgets = filter(widgetsToShow, (_is: any) => {
      let $widgetEl;
      // form widgets already have is-widget-listed attribute
      if (this.isFormOrLiveWidgetType(_is.widgetType)) {
        $widgetEl = _is.$element.parent().closest('[is-widget-listed]');
        if (!$widgetEl.length) {
          let widgetId = _is.$element.attr('widget-id');
          let $el = this.markupManager.processedMarkupForDialog.find('[widget-id="' + widgetId + '"]');
          $widgetEl = $el.parent().closest('[is-widget-listed]');
        }
      } else {
        $widgetEl = _is.$element.closest('[is-widget-listed]');
        if (!$widgetEl.length) {
          let widgetId = _is.$element.attr('widget-id');
          let $el = this.markupManager.processedMarkupForDialog.find('[widget-id="' + widgetId + '"]');
          $widgetEl = $el.closest('[is-widget-listed]');
        }
      }
      return ($widgetEl && $widgetEl.attr('name') === widgetName);

    });

    return filteredWidgets;
  }

  isFormOrLiveWidgetType(widgetType: any) {
    return widgetType === 'wm-form' || widgetType === 'wm-liveform' || widgetType === 'wm-list' || widgetType === 'wm-livefilter';
  }

  /*
   * Initializes widgets and creates widgets-tree
   * @param widgetTree collection received from the Widgets service
   */
  buildWidgets(widgetTreeObject: any, variablesTree: any) {
    this.buildFormWidgetsTree(widgetTreeObject, variablesTree);
    this.buildPageContainerWidgetsTree(widgetTreeObject, variablesTree);
  }

  /*
 * Get the form widgets and construct the tree with the out-bound properties of them
 */
  buildFormWidgetsTree(widgetTreeObject: any, variablesTree: any) {
    // get the form-widgets
    let widgets = byType('form-widgets');
    // reset all the widgets by removing is-widget listed attr.
    if(this.selectedPage && this.selectedPageDom) {
      forEach(widgets, (widget: any) => {
        widget['is-widget-listed'] = false;
      });
    } else {
      forEach(widgets, (widget: any) => {
        $(document).find('main').find('[widgetid=' + widget.widgetId + ']').removeAttr('is-widget-listed')
      });
    }

    /* if binding a widget's property remove it from the collection except for the property limitdisplaytext in textarea*/
    if (this.boundEntityParams?.bindMode === 'widget' && this.boundEntityParams.activeProperty.propertyOwnerName && this.boundEntityParams.activeProperty.name !== 'limitdisplaytext') {
      delete widgets[this.boundEntityParams.activeProperty.propertyOwnerName];
    }

    // iterate through the form-widgets
    forEach(widgets, (widget: any, name: any) => {
      // do not add the widget to widgetTreeObject when widget is already listed i.e. contains is-widget-listed attr.
      if (!$(document).find('main').find('[widgetid=' + widget.widgetId + ']').attr('is-widget-listed')) {
        // get the constructed tree-like structure of out-boundable properties of the widget
        let widgetProps = this.getWidgetPropertiesChildTree(widget, null, variablesTree, widgetTreeObject),
          isTreeExpanded = this.isInBoundPath(name),
          isActive = isTreeExpanded ? this.isActiveEntity(name) : false;
        // Push the widget to the Widgets tree
        widgetTreeObject.push({
          "id": name,
          "class": this.getWidgetClass(widget.widgetType, widget.type),
          "children": this.getTypesBeforeGetWidgetPropertiesChildTree(widget, null, variablesTree, widgetTreeObject),
          "props": { value: name, type: 'widget', "bindable": false },
          "collapsed": !isTreeExpanded,
          "active": isActive,
           ...(widget.widgetType === 'wm-prefab' && {"hasChildren": true, prefabname: widget.attrs?.prefabname || widget.nativeElement.getAttribute('prefabname')})
        });
      }
    });
  }

  getTypesBeforeGetWidgetPropertiesChildTree(widget: any, variable: any, variablesTree: any, widgetTreeObject: any) {
    if (widget && widget.binddataset) {
      return (): Promise<{ id: any; sub: any; class: string; collapsed: boolean; children: any; hasChildren: any; props: { bindable: boolean; value: any; type: any; datatype: any; bindingType: any; isList: any; nodeType: string; }; active: any; }[]> => new Promise(resolve => {
        let WidgetVariable: any = this.getDataTypesOfWidget(widget)
        let service = WidgetVariable?.liveSource || WidgetVariable?.service;
        this.typeService.getDataTypesByService(service, true).then(() => {
          const nodes: any = this.getWidgetPropertiesChildTree(widget, variable, variablesTree, widgetTreeObject);
          resolve(nodes);
        });

      });
    } else {
      return this.getWidgetPropertiesChildTree(widget, variable, variablesTree, widgetTreeObject);
    }

  }
  getDataTypesOfWidget(widgetDetails: any) {
    let bindDataSet = widgetDetails.binddataset
    if (bindDataSet && bindDataSet.startsWith('bind:Variables')) {
     let variableName = bindDataSet.split('.')[1];
     return this.getVariableObject(variableName);
    }
    if (bindDataSet && bindDataSet.startsWith('bind:Widgets')) {
      let widgetList = byType('form-widgets');
      let widgetName = bindDataSet.split('.')[1];
      let nextWidget = widgetList[widgetName];

      // Loop to process nested widgets
      while (nextWidget && nextWidget.binddataset.startsWith('bind:Widgets')) {
        widgetName = nextWidget.binddataset.split('.')[1];
        nextWidget = widgetList[widgetName];
      }

      // Process the final widget or variable
      if (nextWidget && nextWidget.binddataset.startsWith('bind:Variables')) {
        let variableName = nextWidget.binddataset.split('.')[1];
       return this.getVariableObject(variableName);
      }

    }
  }

  getVariableObject(variableName: any) {
    const variableCollectionList = Object.values(this.variableService.variableCollection)
    const variableCollection = Object.assign({}, ...variableCollectionList)
    return variableCollection[variableName];
  }



  //Return class depending on widgetType and type
  getWidgetClass(widgetType: any, type: any) {
    return 'wms-exp-editor wms-widget-' + (widgetType === 'wm-chart' ? type.toLowerCase() + 'chart' : widgetType.substring(3, widgetType.length));
  }

  /**
   * returns isList of the node based on 'getIsListFrom'
   * @param getIsListFrom expression based on which isList has to be evaluated
   * @param widget scope of the current widget
   * @returns {boolean}
   */
  evalIsListFromExp(getIsListFrom: any, widget: any) {
    return widget && this.execBindedFunction(widget, getIsListFrom.replace('expr:', ''));
  }

  /**
   * returns type of the node based on 'getTypeFrom'
   * @param getTypeFrom expression based on which type has to be evaluated
   * @param widget scope of the current widget
   * @param scope parent widget's scope
   * @returns {boolean}
   */
  evalTypeFromExp(getTypeFrom: any, widget: any, scope?: any) {
    let propertyType;
    if (stringStartsWith(getTypeFrom, 'expr:')) {
      propertyType = this.execBindedFunction(widget,getTypeFrom.replace('expr:', ''));
    } else {
      propertyType = this.typeService.getTypeForExpression(widget['bind' + getTypeFrom], widget, undefined, scope);
    }
    return propertyType;
  }

  execBindedFunction(widget: any, methodDetails: any) {
    let regex = /\(([^()]*)\)/g,
        methodArgMatch = methodDetails.match(regex),
        methodArg,
        types: any = this.typeService.getTypes();

    if (methodArgMatch) {
      methodArg = methodArgMatch[0];
      let methodName = methodDetails.replace(methodArg, '');
      if(widget[methodName]){
        return widget[methodName](methodArg.replace('(', '').replace(')', '').replaceAll("'", ''),types);
      }
    } else {
      return widget[methodDetails];
    }

  }

  /*
   * takes a subtree from VariablesTree and makes it compatible for WidgetsTree
   * @param variableTree the subtree from VariablesTree
   * @param widgetName the name of widget for which tree will be made
   * @param propertyName the widget property to which tree will be appended
   */
  widgetCompatibleTree(variableTree: any, widgetName: any, propertyName: any, prefix?: any) {
    let widgetTree = getClonedObject(variableTree);
    forEach(widgetTree, (nodeObject: any) => {
      // prefix is supposed to be added for inner tree nodes.
      nodeObject.props.value = prefix ? (prefix + '.' + widgetName + "." + propertyName + "." + nodeObject.id) : (widgetName + "." + propertyName + "." + nodeObject.id);
      nodeObject.props.type = 'widget';
      nodeObject.children = this.widgetCompatibleTree(nodeObject.children, widgetName, propertyName + "." + nodeObject.id + (nodeObject.props.isList ? '[$i]' : ''));
      nodeObject.collapsed = !this.isInBoundPath(nodeObject.props.value);
      nodeObject.active = !nodeObject.collapsed ? this.isActiveEntity(nodeObject.id) : false;
      if (nodeObject.active) {
        this.validateBinding(nodeObject.id, nodeObject.props);
      }
    });

    return widgetTree;
  }

  /*
   * prepares the subtree for a prefab's exposed property bound to a variable in prefab
   * @param prefab scope
   * @param propertyName the prefab property to which tree will be appended
   */
  getPrefabPropertySubTree(prefab: any, propertyName: any) {
    let requiredTree = this.getSubTree(prefab[propertyName], {}),
      subTree = this.widgetCompatibleTree(requiredTree, prefab.name, propertyName);
    return subTree;
  }

  /*
   * loop over each widget's properties to append bindable properties to the tree object
   * Prefix will be added to node property of the tree.
   * For example, if the Prefix is _myPrefix_, node value will be _myPrefix_.WidgetName.propertyName
   */
  getWidgetPropertiesChildTree(widget: any, prefix: any, variablesTree: any, widgetTreeObj?: any, scope?: any) {
    let propertySubtree: any,
      result: any,
      propertyBindingType: any,
      widgetProps: any = [],
      types: any = this.typeService.getTypes(),
      listWidgets: any = [];
    // Retrieve base properties
    let baseWidgetProps: any = getWidgetPropsByType('wm-' + widget.base) || {};
    let datavalueProp = baseWidgetProps.datavalue ? { datavalue: baseWidgetProps.datavalue } : {};
    const allProperties = Object.assign({}, widget.widgetProps, datavalueProp);

    forEach(allProperties, (property: any, propertyName: any) => {
      let isTreeExpanded,
        isActive,
        bindValue,
        nodeObj,
        propertyType,
        childrenFields,
        hasChildren,
        isList,
        widgetsToShow,
        fieldsToShow,
        getTypeFrom = property.getTypeFrom,
        getIsListFrom = property.getIsListFrom;

      //If bind target is widget map and property source is dataset don't populate widgets
      if (property.isWidgetMap && this.boundEntityParams?.activeProperty.name === 'dataset') {
        return;
      }
      if (property.bindable === 'out-bound' || property.bindable === 'in-out-bound') {
        propertySubtree = null;
        isList = property.isList || includes(property.type, 'array');
        if (getIsListFrom) {
          isList = this.evalIsListFromExp(getIsListFrom, widget);
        }
        /*
         * If current property's type is dependent on :
         * - another property of widget, like dataset, evaluate the type of source of that dependent property
         * - an expression in the widget's scope, evaluate the expression
         */
        if (getTypeFrom) {
          propertyType = this.evalTypeFromExp(getTypeFrom, widget, scope);
          childrenFields = get(types, '["' + propertyType + '"].fields');
          hasChildren = childrenFields && keys(childrenFields).length;
        }

        propertyBindingType = this.getBindingType(propertyType || property.type, isList, true);

        /**if child elements are bound to the widget outbound data for Model variable, generate the subtree based on the metadata from the dataset of the widget**/
        if (widget.variableType === 'wm.Variable') {
          result = this.getSubTree(widget.variable.dataSet, {}, this.boundEntityParams?.boundEntity);
          propertySubtree = this.widgetCompatibleTree(result, widget.name, propertyName, prefix);
        }

        if (widget.widgetType === 'wm-prefab') {
          if (stringStartsWith(property.__value, "bind:Variables.")) {
            result = this.getPrefabPropertySubTree(widget, propertyName);
            propertySubtree = result;
            propertyBindingType = 'object';
          } else if (stringStartsWith(property.__value, "bind:Widgets.") && (propertyBindingType || includes(property.type, 'object'))) {
            // generate metadata for prefab outbound property, only if it's data type is not primitive
            let propertyValue = property.__value && property.__value.split('.') && property.__value.split('.'),
              widgetName = propertyValue.length && propertyValue[1],
              dataset = get(widget, 'pfScope.Widgets[' + widgetName + '].dataset');
            // If property bound to child node of dataset, we should show child node properties as dataset
            if (dataset && propertyValue.length > 3) {
              if (isArray(dataset)) {
                dataset = get(dataset[0], propertyValue.slice(3).join('.'));
              } else if (isObject(dataset)) {
                dataset = get(dataset, propertyValue.slice(3).join('.'));
              }
            }
            /**if child elements are bound to the prefab outbound data, generate the subtree based on the metadata from the dataset of the widget from the prefab scope**/
            result = dataset && this.getSubTree(dataset, {});
            propertySubtree = this.widgetCompatibleTree(result, widget.name, propertyName, prefix);
          }
        }

        //For livelist add widgets in its isolatescope to selecteditemwidgets node while populating widgets tree
        if (widget.widgetType === 'wm-list' && includes(['selectedItemWidgets', 'currentItemWidgets'], propertyName)) {
          result = [];
          let elements: JQuery<HTMLElementTagNameMap[keyof HTMLElementTagNameMap]>;
          if(this.selectedPage && this.selectedPageDom) {
            elements = this.selectedPageDom('[name=' + widget.name + '] .app-list-item:eq(0) *');
          } else {
            //elements = $('[name=' + widget.name + '] .app-list-item:eq(0)').find('[widget-id]');
            // @ts-ignore
            elements = this.markupManager.getMarkup()?.find('[name="' + widget.name + '"] > wm-listtemplate').find('[widget-id]')
          }
          forEach(elements, (ele: any) => {
            let $target: any = ele,
              innerValue,
              _is: any,
              isWidgetTreeExpanded;
            if (propertyName === 'selectedItemWidgets') {
              if (widget.multiselect) {
                innerValue = 'selectedItemWidgets[$i]';
                isList = true;
                property.type = 'array';
              } else {
                innerValue = 'selectedItemWidgets';
                isList = false;
                property.type = 'object';
              }
            } else {
              innerValue = 'currentItemWidgets';
            }
            if (!$target.widget) {
              let registryById = getWidgetRegistry();
              $target.widget = registryById.get($target.getAttribute('widget-id'));
            }
            if(this.selectedPage && this.selectedPageDom) {
              const allWidgets = getWidgetRegistry();
              allWidgets.forEach(wid => {
                if(wid.name === $target.attribs.name) {
                  _is = wid;
                }
              });
            } else if ($target.widget) {
              _is = $target.widget;
            }
            if (_is) {
              // adding children for all formWidgets including list.
              if (_is.name && _is.widgetType && includes(getTypes('form-widgets'), _is.widgetType)) {
                isWidgetTreeExpanded = this.isInBoundPath(_is.name);
                result.push({
                  "id": _is.name,
                  "class": this.getWidgetClass(_is.widgetType, _is.type),
                  "children": this.getWidgetPropertiesChildTree(_is, prefix ? (prefix + '.' + widget.name + '.' + innerValue) : (widget.name + '.' + innerValue), variablesTree),
                  "props": { value: _is.name + _is.$id, type: 'widget', "bindable": false },
                  "collapsed": !isWidgetTreeExpanded,
                  "active": isWidgetTreeExpanded ? this.isActiveEntity(_is.name) : false,
                  "widgetId": _is.widgetId
                });
                if(this.selectedPage && this.selectedPageDom) {
                  _is['is-widget-listed'] = true;
                } else {
                  $target.setAttribute("is-widget-listed",true);
                }
              }
              if (_is.widgetType && _is.widgetType === 'wm-list') {
                $($target).find('[widget-id]').each((i: number, el: any) => {
                  let $listTarget: any = el;
                  if ($listTarget.widget) {
                    listWidgets.push($listTarget.widget);
                  }
                });
              }
            }
          });

          //Remove duplicate widgets from widget tree object
          if (result.length && widgetTreeObj) {
            remove(widgetTreeObj, (treeWidget: any) => {
              return find(result, { 'id': treeWidget.id });
            });
            //Remove widgets of nested list widgets
            remove(result, (widget: any) => {
              return find(listWidgets, { 'widgetid': widget.widgetId });
            });
          }

          propertySubtree = result;
        }

        //Is widget map type property and form or filter field widgets populate the widgets tree
        if (property.isWidgetMap && widget.widgetType !== 'wm-list') {
          result = [];
          widgetsToShow = this.getWidgetsToShow(widget.name);
          forEach(widgetsToShow, (_is: any) => {
            let isWidgetTreeExpanded = this.isInBoundPath(_is.name);
            result.push({
              'id': _is.name,
              'class': this.getWidgetClass(_is.widgetType, _is.type),
              'children': this.getWidgetPropertiesChildTree(_is, prefix ? (prefix + '.' + widget.name + '.' + propertyName) : (widget.name + '.' + propertyName), variablesTree),
              'props': { value: _is.name + _is.$id, type: 'widget', bindable: false },
              'collapsed': !isWidgetTreeExpanded,
              'active': isWidgetTreeExpanded ? this.isActiveEntity(_is.name) : false,
              'widgetId': _is.widgetId
            });
          });

          //Prevent widgets added in liveform/livefilter getting added outside
          if (result.length && widgetTreeObj) {
            remove(widgetTreeObj, (treeWidget: any) => {
              return find(result, { 'id': treeWidget.id });
            });
          }

          propertySubtree = result;
        }

        bindValue = prefix ? (prefix + '.' + widget.name + '.' + propertyName) : (widget.name + '.' + propertyName);

        isTreeExpanded = this.isInBoundPath(bindValue);
        isActive = isTreeExpanded ? this.isActiveEntity(propertyName) : false;

        // If pre-expansion is required for this node, get its children nodes
        if (isTreeExpanded && !isActive && !property.isWidgetMap) {
          //For form/liveform's dataoutput show fields which are selected
          if (propertyName === 'dataoutput') {
            fieldsToShow = this.getFieldsToShow(widget.name);
          }
          if (propertyType && propertyType != 'entry') {
            propertySubtree = this.getTreeForTypeCB(propertyType, {
              parentNodeLabel: bindValue + (isList ? '[$i]' : ''),
              componentType: 'widget',
              fieldsToShow: fieldsToShow
            });
          }
        }

        nodeObj = {
          'id': propertyName,
          'sub': this.getNodeInfoLabel(propertyType || property.type, propertyBindingType),
          'class': 'wms-exp-editor',
          'bindable': true,
          'hasChildren': hasChildren,
          'props': {
            'value': bindValue,
            'type': 'widget',
            'bindable': !property.isWidgetMap,
            'datatype': propertyType || property.type,
            'bindingType': propertyBindingType,
            'isList': isList
          },
          'children': propertySubtree,
          'collapsed': !isTreeExpanded,
          'active': isActive
        };
        widgetProps.push(nodeObj);
        if (isActive) {
          this.validateBinding(nodeObj.id, nodeObj.props);
        }
      }
    });
    return widgetProps;
  }

  /*
   * Get the page-container-widgets and construct the tree with the out-bound properties of them
   */
  buildPageContainerWidgetsTree(widgetTreeObject: any, variablesTree: any) {
    // get the page-container-widgets
    let widgets = byType('page-container-widgets');

    // iterate through the page-container-widgets
    forEach(widgets, (widget: any, name: any) => {

      // if the page-container widget is including a partial, iterate through the widgets inside the partial and construct a tree-like structure.
      if (isDefined(widget.Widgets) && widget['page-container'] && Object.keys(widget.Widgets).length) {

        let childWidgets: any = [],
          isTreeExpanded,
          isActive;

        // For each page-container widget, iterate through the widgets inside the partial inside it.
        forEach(widget.Widgets, (childWidget: any, childWidgetName: any) => {

          // get the constructed tree-like structure of out-boundable properties of the widget
          //Pass included page/partial's scope to fetch variables
          let childWidgetChildren = this.getWidgetPropertiesChildTree(childWidget, name + '.' + 'Widgets', variablesTree, undefined, childWidget.viewParent),
            isTreeExpandedChild = this.isInBoundPath(childWidgetName),
            isActiveChild = isTreeExpandedChild ? this.isActiveEntity(childWidgetName) : false;

          if (childWidgetChildren.length) {
            childWidgets.push({
              "id": childWidgetName,
              "class": this.getWidgetClass(childWidget.widgetType, childWidget.type),
              "children": childWidgetChildren,
              "props": { value: childWidgetName, type: 'widget', "bindable": false },
              "collapsed": !isTreeExpandedChild,
              "active": isActiveChild
            });
          }
        });
        isTreeExpanded = this.isInBoundPath(name);
        isActive = isTreeExpanded ? this.isActiveEntity(name) : false;

        // Push the widget to the Widgets tree
        if (childWidgets.length) {
          widgetTreeObject.push({
            "id": name,
            "class": 'wms-exp-editor wms-widget-' + widget.widgetType.substring(3, widget.widgetType.length),
            "children": childWidgets,
            "props": { value: name, type: 'widget', "bindable": false },
            "collapsed": !isTreeExpanded,
            "active": isActive
          });
        }
      }
    });
  }

  getBindMap = (type: any, parentReference: any, oldBindings?: any, visitedNodes?: any) => {
    let curFieldObj, types = this.typeService.getTypes();
    oldBindings = oldBindings || {},
      visitedNodes || (visitedNodes = []),
      -1 === visitedNodes.indexOf(type) && types && types[type] && types[type].fields && Object.keys(types[type].fields).length && (visitedNodes.push(type),
        parentReference.fields = [],
        forEach(types[type].fields, (field: any, fieldName: any) => {
          curFieldObj = {
            name: fieldName,
            type: field.type,
            isList: field.isList
          },
            oldBindings[fieldName] && !isObject(oldBindings[fieldName]) && (curFieldObj.name = oldBindings[fieldName]),
            parentReference.fields.push(curFieldObj),
            this.getBindMap(field.type, curFieldObj, oldBindings[fieldName], visitedNodes)
        }))
  }
}
